import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Divider from '@material-ui/core/Divider';
import Grow from '@material-ui/core/Grow';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { OutlinedTextFieldProps } from '@material-ui/core/TextField/TextField';
import SearchIcon from '@material-ui/icons/Search';
import React, { ChangeEvent, MouseEvent, ReactNode, useLayoutEffect, useRef, useState  } from 'react';

import { Omit } from '../helpers/typeHelpers';
import { IPaging } from '../modules/base/models/Paging';
import TextField from './TextField';

const useStyles = makeStyles((theme: Theme) => createStyles({
  textField: {
    marginTop: 0,
    marginBottom: 0,
    '& > div': {
      paddingRight: 0
    }
  },
  suggestionContent: {
    zIndex: 999
  },
  adornment: {
    marginRight: theme.spacing(2)
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

export interface ISuggestion {
  label: string | ReactNode;
  value: any;
}

interface IProps extends Omit<OutlinedTextFieldProps, 'variant' | 'classes'> {
  suggestions?: ISuggestion[];
  loading?: boolean;
  onSelect?: (value: any) => void;
  hasRequiredError?: boolean;
  helperText?: string;
  paging?: IPaging;
  onNextPage?: () => void;
}

const AutoSuggest = ({
  className,
  label,
  hasRequiredError,
  helperText,
  suggestions,
  loading,
  onChange,
  onSelect,
  paging,
  onNextPage,
  ...props
}: IProps) => {
  const [value, setValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(0);
  const [focus, setFocus] = useState(false);
  const [anchorEl, setAnchorEl] = useState<any>(undefined);
  const wrapperEl = useRef<HTMLDivElement>(null);
  const paperEl = useRef<HTMLDivElement>(null);
  const loadingEl = useRef<HTMLLIElement >(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const classes = useStyles();

  const suggestionLength = (suggestions || []).length;

  const handleClose = (event: ChangeEvent<{}> | MouseEvent<HTMLElement, MouseEvent>) => {
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }

    setFocus(false);
  };

  const handleChange = (event: any) => {
    setValue(event.target.value);

    if (typeof onChange !== 'undefined') {
      onChange(event);
    }

    setFocus(true);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const loadingDimensions = loadingEl?.current?.getBoundingClientRect();
    const paperDimensions = paperEl?.current?.getBoundingClientRect();

    if (
      typeof onNextPage !== 'undefined' &&
      suggestionLength < Number(paging?.totalItems) &&
      !loading &&
      focus &&
      (!!props.value || !!value) &&
      suggestionLength > 0 &&
      loadingDimensions &&
      paperDimensions &&
      loadingDimensions.bottom - paperDimensions.bottom < 30
    ) {
      onNextPage();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' && suggestions && selectedValue < suggestions.length - 1) {
      setSelectedValue(selectedValue + 1);
    }
    if (event.key === 'ArrowUp' && suggestions && selectedValue > 0) {
      setSelectedValue(selectedValue - 1);
    }
    if (event.key === 'Enter' && suggestions  && suggestionLength > 0) {
      event.preventDefault();
      handleSelect(suggestions[selectedValue].value);
    }
  };

  const handleSelect = (selectedItem: any) => {
    if (typeof onSelect !== 'undefined') {
      onSelect(selectedItem);
    }

    setValue('');
    setFocus(false);
  };

  useLayoutEffect(() => {
    setWidth(wrapperEl?.current?.getBoundingClientRect().width);
  });

  const HighLightSuggestions =
    (suggestionContent: ISuggestion[]): ISuggestion[] =>
    suggestionContent.map(suggestion => {
      let index = -1;
      if (typeof suggestion.label === 'string') {
        index = (suggestion.label.toLocaleLowerCase() || '').indexOf(value.toLocaleLowerCase());
      }
      return {
        label: (
          <div>
            {typeof suggestion.label !== 'string' ? (
              suggestion.label
            ) : index <= -1 ? (
              <span>{suggestion.label}</span>
            ) : (
              <span>
                <span>{(suggestion.label || '').substr(0, index)}</span>
                  <span>
                    <b>
                      {(suggestion.label || '').substr(index, value.length)}
                    </b>
                  </span>
                <span>{(suggestion.label || '').substr(index + value.length)}</span>
              </span>
            )}
          </div>
        ),
        value: suggestion.value
      };
  });

  return (
    <div ref={wrapperEl}>
      <TextField
        onKeyDown={handleKeyDown}
        inputRef={node => setAnchorEl(node)}
        value={props.value || value}
        onFocus={() => setFocus(true)}
        className={`${className} ${classes.textField}`}
        label={label}
        onChange={handleChange}
        error={hasRequiredError}
        displayRequiredErrorMessege={!!helperText}
        InputLabelProps={{shrink: focus || loading || !!props.value || !!value}}
        helperText={helperText}
        endPlacement={(
          <InputAdornment position='end' className={classes.adornment}>
              {loading ? (
                  <CircularProgress size={16} />
              ) : (
                  <SearchIcon />
              )}
            </InputAdornment>
        )}

        {...props}
      />
      <Popper
        open={focus && (!!props.value || !!value) && suggestionLength > 0}
        anchorEl={anchorEl}
        transition
        disablePortal
        className={classes.suggestionContent}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper
              ref={paperEl}
              onScroll={handleScroll}
              square
              style={{
                overflow: 'auto',
                maxHeight: 200,
                marginTop: 8,
                marginLeft: anchorEl && width ? width - anchorEl.clientWidth : undefined,
                width
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {(HighLightSuggestions(suggestions || []) || []).map((suggestion, index) => (
                    <MenuItem
                      selected={index === selectedValue}
                      key={index}
                      onClick={() => handleSelect(suggestion.value)}
                    >
                      {suggestion.label}
                    </MenuItem>
                  ))}
                  {typeof onNextPage !== 'undefined' && suggestionLength < Number(paging?.totalItems) && (
                    <MenuItem ref={loadingEl}>
                      <CircularProgress size={16} />
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default AutoSuggest;
